import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import type { PackageView, ServiceItemView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import {
  adminPackagesQuery,
  createPackage,
  stayBillingKeys,
  updatePackage,
} from '../api/inpatient-billing.ts';
import { can, sessionQuery } from '../api/session.ts';
import { ServicePicker } from '../billing/service-picker.tsx';
import { useOnline } from '../hooks/use-online.ts';
import styles from '../inpatient/inpatient.module.css';
import { SettingsPage } from '../routes/settings-nav.tsx';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';

// Settings > Packages (docs/plans/phase2-inpatient-billing.md §6): fixed-price bundles for a
// procedure or a stay. A package is charged as a catalogue service, priced in the price
// lists like any other, and replaces the items it covers up to their limits.

interface CoverDraft {
  item: { id: string; code: string; name: string };
  /** Digits as typed; empty means no limit. */
  max: string;
}

function coversText(p: PackageView): string {
  return p.covers
    .map((c) => (c.maxQuantity === null ? c.name : `${c.name}, up to ${String(c.maxQuantity)}`))
    .join('; ');
}

function PackageList() {
  const online = useOnline();
  const queryClient = useQueryClient();
  const packages = useQuery(adminPackagesQuery);
  const toggle = useMutation({
    mutationFn: (p: PackageView) =>
      updatePackage(p.id, { status: p.status === 'active' ? 'inactive' : 'active' }),
    onSuccess: async (p) => {
      await queryClient.invalidateQueries({ queryKey: stayBillingKeys.adminPackages });
      await queryClient.invalidateQueries({ queryKey: stayBillingKeys.packages });
      toasts.show({
        title: p.status === 'active' ? 'Package switched on' : 'Package switched off',
        description: p.name,
      });
    },
  });
  return (
    <Section title="Packages">
      {packages.isPending ? <LoadingBlock label="Loading packages" rows={3} /> : null}
      {packages.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void packages.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(packages.error, "Packages couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {packages.isSuccess ? (
        <Table flat dense aria-label="Packages">
          <TableHeader>
            <Column isRowHeader>Package</Column>
            <Column>Charged as</Column>
            <Column>Covers</Column>
            <Column>State</Column>
            <Column>Action</Column>
          </TableHeader>
          <TableBody
            items={packages.data.items}
            renderEmptyState={() => 'No packages yet. Add the first one below.'}
          >
            {(p) => (
              <Row id={p.id}>
                <Cell>{p.name}</Cell>
                <Cell>
                  <span className="wodi-id">{p.item.code}</span> {p.item.name}
                </Cell>
                <Cell>{coversText(p)}</Cell>
                <Cell>
                  <Tag state={p.status === 'active' ? 'active' : 'neutral'}>
                    {p.status === 'active' ? 'On' : 'Off'}
                  </Tag>
                </Cell>
                <Cell>
                  <Button
                    dense
                    variant="quiet"
                    isDisabled={!online}
                    onPress={() => {
                      toggle.mutate(p);
                    }}
                  >
                    {p.status === 'active' ? 'Switch off' : 'Switch on'}
                  </Button>
                </Cell>
              </Row>
            )}
          </TableBody>
        </Table>
      ) : null}
      {toggle.isError ? (
        <Notice tone="error">
          {errorText(toggle.error, "The package wasn't changed. Try again.")}
        </Notice>
      ) : null}
    </Section>
  );
}

function AddPackage() {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [item, setItem] = useState<ServiceItemView | null>(null);
  const [covers, setCovers] = useState<CoverDraft[]>([]);
  const [tried, setTried] = useState(false);
  const badLimit = covers.some((c) => c.max.trim() !== '' && !/^[1-9]\d{0,4}$/.test(c.max.trim()));
  const ready = name.trim() !== '' && item !== null && covers.length > 0 && !badLimit;
  const add = useMutation({
    mutationFn: () =>
      createPackage({
        name: name.trim(),
        serviceItemId: item?.id ?? '',
        covers: covers.map((c) => ({
          serviceItemId: c.item.id,
          maxQuantity: c.max.trim() === '' ? null : Number(c.max.trim()),
        })),
      }),
    onSuccess: async (p) => {
      await queryClient.invalidateQueries({ queryKey: stayBillingKeys.adminPackages });
      await queryClient.invalidateQueries({ queryKey: stayBillingKeys.packages });
      toasts.show({ title: 'Package added', description: p.name });
      setName('');
      setItem(null);
      setCovers([]);
      setTried(false);
    },
  });
  return (
    <Section
      title="Add a package"
      description="Add the package's service and its prices in Services and prices first, then choose what it covers."
    >
      <form
        className={styles.form}
        aria-label="Add a package"
        onSubmit={(e) => {
          e.preventDefault();
          setTried(true);
          if (ready) add.mutate();
        }}
      >
        <div className={styles.row}>
          <TextField
            label="Package name"
            value={name}
            onChange={setName}
            {...(tried && name.trim() === '' ? { errorMessage: 'Enter a name.' } : {})}
          />
        </div>
        {item === null ? (
          <ServicePicker
            label="Charged as"
            description="The catalogue service the package is billed as, with its own prices."
            onSelect={setItem}
          />
        ) : (
          <p className={styles.meta}>
            {'Charged as '}
            <span className="wodi-id">{item.code}</span>
            {` ${item.name}. `}
            <Button
              dense
              variant="quiet"
              onPress={() => {
                setItem(null);
              }}
            >
              Change
            </Button>
          </p>
        )}
        <ServicePicker
          label="Add an item it covers"
          description="Covered charges on a stay are replaced by the package."
          onSelect={(chosen) => {
            setCovers((list) =>
              list.some((c) => c.item.id === chosen.id)
                ? list
                : [
                    ...list,
                    { item: { id: chosen.id, code: chosen.code, name: chosen.name }, max: '' },
                  ],
            );
          }}
        />
        {covers.length === 0 ? (
          <p className={styles.meta}>
            {tried ? 'Choose at least one item the package covers.' : 'No covered items yet.'}
          </p>
        ) : (
          <ul className={styles.form} aria-label="Covered items">
            {covers.map((c, index) => (
              <li key={c.item.id} className={styles.row}>
                <span>
                  <span className="wodi-id">{c.item.code}</span> {c.item.name}
                </span>
                <TextField
                  label={`Most units covered for ${c.item.name}`}
                  description="Leave empty for no limit."
                  inputMode="numeric"
                  value={c.max}
                  onChange={(value) => {
                    setCovers((list) =>
                      list.map((x, i) => (i === index ? { ...x, max: value } : x)),
                    );
                  }}
                  {...(c.max.trim() !== '' && !/^[1-9]\d{0,4}$/.test(c.max.trim())
                    ? { errorMessage: 'A whole number above zero, or empty.' }
                    : {})}
                />
                <Button
                  dense
                  variant="quiet"
                  onPress={() => {
                    setCovers((list) => list.filter((_, i) => i !== index));
                  }}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
        {add.isError ? (
          <Notice tone="error">
            {errorText(add.error, "The package wasn't added. Try again.")}
          </Notice>
        ) : null}
        <div className={styles.actions}>
          <Button
            type="submit"
            variant="primary"
            isDisabled={!online}
            isPending={add.isPending}
            pendingLabel="Adding…"
          >
            Add package
          </Button>
        </div>
      </form>
    </Section>
  );
}

export function PackagesSettingsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  if (session === null || !can(session, 'billing.packages.manage'))
    return (
      <>
        <PageHeader title="Packages" />
        <Notice tone="info">
          You don't have permission to set up packages. Ask an administrator if you need it.
        </Notice>
      </>
    );
  return (
    <SettingsPage title="Packages">
      <PackageList />
      <AddPackage />
    </SettingsPage>
  );
}
